import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "../i18n/LangContext";
import { api } from "../api/client";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Bell, CheckCheck } from "lucide-react";

const TYPE_COLORS = {
  PAYMENT_APPROVED: "text-emerald-300",
  PAYMENT_REJECTED: "text-red-300",
  PAYMENT_MISMATCH: "text-amber-300",
  ENROLLMENT_CREATED: "text-sky-300",
  ENROLLMENT_ACTIVATED: "text-emerald-300",
  COACH_CANDIDATE_APPROVED: "text-emerald-300",
  COACH_CANDIDATE_REJECTED: "text-red-300",
  COACH_QUALIFICATION_COMPLETE: "text-emerald-300",
  COACH_INVITED: "text-sky-300",
  ROLE_CHANGED: "text-purple-300",
};

export function NotificationBell() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/me?limit=10");
      setItems(data.data.items);
      setUnread(data.data.unread_count);
    } catch (err) {
      console.error("loading notifications failed", err);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  const open = async (n) => {
    if (!n.read) {
      try { await api.post(`/notifications/${n.notification_id}/read`); }
      catch (err) { console.error("marking notification read failed", err); }
      load();
    }
    if (String(n.type).startsWith("PAYMENT") || String(n.type).startsWith("ENROLLMENT")) navigate("/my-journey");
  };

  const readAll = async () => {
    try { await api.post("/notifications/read-all"); }
    catch (err) { console.error("marking all notifications read failed", err); }
    load();
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button data-testid="notification-bell-btn" variant="ghost" size="icon" className="relative h-9 w-9 text-slate-300 hover:bg-white/5 hover:text-white">
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span data-testid="notification-unread-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF3B30] text-white text-[10px] font-bold flex items-center justify-center">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[min(360px,calc(100vw-2rem))] p-0 bg-[#161923] border-white/10 text-slate-200">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
          <span className="text-sm font-semibold text-white">{t("notifications.title")}</span>
          {unread > 0 && (
            <button data-testid="notification-read-all-btn" onClick={readAll} className="text-xs text-[#10B981] hover:text-emerald-300 flex items-center gap-1">
              <CheckCheck className="h-3.5 w-3.5" /> {t("notifications.readAll")}
            </button>
          )}
        </div>
        <div className="max-h-[60vh] overflow-y-auto divide-y divide-white/5">
          {items.length === 0 ? (
            <p data-testid="notification-empty" className="px-4 py-8 text-center text-sm text-slate-500">{t("notifications.empty")}</p>
          ) : items.map((n) => (
            <button
              key={n.notification_id}
              data-testid={`notification-item-${n.notification_id}`}
              onClick={() => open(n)}
              className={`w-full text-left px-4 py-3 hover:bg-white/[0.03] transition ${n.read ? "opacity-60" : ""}`}
            >
              <div className="flex items-start gap-2">
                {!n.read && <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#FF3B30] shrink-0" />}
                <div className="min-w-0">
                  <div className={`text-sm font-medium ${TYPE_COLORS[n.type] || "text-white"}`}>{n.subject}</div>
                  {n.message && <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">{n.message}</p>}
                  <div className="text-[10px] uppercase tracking-wider text-slate-600 mt-1">{new Date(n.created_at).toLocaleString()}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
